/**
 * Sentry Client-Side Configuration
 *
 * This file configures Sentry for browser/client-side error tracking.
 * Runs in the user's browser and captures frontend errors.
 */

import * as Sentry from "@sentry/react"

import {
  scrubEmbedIdentityFromBreadcrumb,
  scrubEmbedIdentityFromEvent
} from "@/common/observability/scrub-embed-identity"
import {
  createTracesSampler,
  DEFAULT_TRACES_SAMPLE_RATE,
  normalizeEmbedTransactionName,
  usableSampleRate
} from "@/common/observability/trace-sampling"
import { hasStaleChunkRecoveryFailed, isStaleChunkError } from "@/common/utils/stale-chunk.util"

/**
 * The message a Sentry event carries, for events that never had a JS error
 * object — Sentry's own global handlers build one from the wire format.
 *
 * `hint.originalException` is the real error and is what we prefer. It is absent
 * for an event Sentry assembled itself, and those are exactly the stale-chunk
 * events this policy is about, so falling back to the serialised value is not an
 * edge case here — it is the common path.
 */
const eventMessage = (event: Sentry.ErrorEvent): string => {
  const value = event.exception?.values?.[0]
  if (value) return `${value.type ?? ""}: ${value.value ?? ""}`
  return event.message ?? ""
}

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const SENTRY_ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_NODE_ENV || "development"
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE
// Clamped rather than passed through: an unparseable rate is NOT a fallback in
// this SDK, it silently drops every trace. `trace-sampling.ts` carries the
// measurement.
const SENTRY_TRACES_SAMPLE_RATE = usableSampleRate(
  parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || String(DEFAULT_TRACES_SAMPLE_RATE)),
  DEFAULT_TRACES_SAMPLE_RATE
)
const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0.1"
)
const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || "1.0"
)

/**
 * Errors dropped before they reach Sentry.
 *
 * Exported so the rules can be asserted directly — a filter that silently
 * swallows a real bug is invisible by construction, which is exactly what
 * happened here before (see the note on the fetch entry below).
 */
export const IGNORED_ERRORS = [
  // Random browser extensions
  "top.GLOBALS",
  "originalCreateNotification",
  "canvas.contentDocument",
  "MyApp_RemoveAllHighlights",
  "atomicFindClose",

  // Network errors that are not actionable.
  //
  // String entries match by SUBSTRING, which made a bare "Failed to fetch"
  // swallow "Failed to fetch dynamically imported module: …" as well — the
  // stale-chunk failure that was replacing the app with a raw error page for
  // users. That is why the bug was invisible in Sentry the whole time it was
  // happening.
  //
  // The negative lookahead excludes exactly that message and nothing else,
  // rather than anchoring on the end of the string. Anchoring would break on
  // the SDK's own rewriting: @sentry/core's fetch instrumentation replaces
  // "Failed to fetch" with "Failed to fetch (host)" to say which call died
  // (see instrument/fetch.js). It only rewrites the bare message, so the
  // dynamic-import variant stays verbatim and stays visible.
  //
  // It stays OUT of this list deliberately, and `beforeSend` is where the
  // stale-chunk decision is made instead. The difference is the whole point: an
  // `ignoreErrors` entry is unconditional and would restore the blindness this
  // carve-out was added to fix, whereas `beforeSend` can ask whether the reload
  // actually recovered and keep the broken-deploy case (NEX-3948).
  // 🔴 THIS LIST IS BROWSER-ASYMMETRIC, SO EVENT VOLUMES HERE CANNOT BE READ
  // AS A BROWSER BREAKDOWN. `Failed to fetch` is CHROME's wording for a fetch
  // that never completed; SAFARI says `Load failed`, which no entry below
  // matches. The identical defect is therefore dropped on Chrome and reported
  // on Safari, and anything measured from the survivors looks Safari-weighted
  // no matter which browsers are actually failing.
  //
  // NEX-3947 was filed on exactly that reading — "heavily weighted to Safari
  // and Mobile Safari … the browser skew suggests a real behavioural
  // difference" — and the skew is this list, not the bug.
  //
  // Deliberately NOT "fixed" by adding `Load failed` here: that would make the
  // two symmetric by blinding both, and the embed bootstrap is being actively
  // diagnosed. `embed-bootstrap.ts` takes the other route instead — it throws
  // a message of its own that matches NEITHER entry, so that one call site is
  // counted identically everywhere. Making the rest of the app symmetric is a
  // reporting-policy decision, not a drive-by.
  "NetworkError",
  "Network request failed",
  /Failed to fetch(?! dynamically imported module)/,

  // ResizeObserver errors (not critical)
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",

  // Common non-critical errors
  "Non-Error promise rejection captured",
  "AbortError: The operation was aborted"
]

if (!SENTRY_DSN) {
  if (SENTRY_ENVIRONMENT !== "development" && SENTRY_ENVIRONMENT !== "local") {
    console.warn(
      "⚠️  Sentry disabled - VITE_SENTRY_DSN not configured. Error tracking will not work."
    )
  }
} else {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,

    // Performance monitoring.
    //
    // `tracesSampler`, not `tracesSampleRate` — the flat rate was spending
    // 98.56% of the org's span quota on the embed widget while six Nexus-app
    // routes had a single sampled event a week. `trace-sampling.ts` carries the
    // measurement, the ratio and why it is one env var rather than two.
    //
    // `tracesSampleRate` is deliberately GONE rather than left beside this: v10
    // ignores it whenever a sampler is defined (`types-hoist/options.d.ts`), so
    // leaving it would be a line that reads as the live rate and is not.
    tracesSampler: createTracesSampler(SENTRY_TRACES_SAMPLE_RATE),

    // Session replay configuration
    replaysSessionSampleRate: SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,

    // Integrations
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true
      }),
      Sentry.browserTracingIntegration()
    ],

    // Filter sensitive data before sending
    beforeSend(event, hint) {
      // A stale chunk that RECOVERED is not an event. See
      // `hasStaleChunkRecoveryFailed` for why the failed case still is, and why
      // the two cannot be told apart by the message alone.
      //
      // This lives in `beforeSend` rather than in `ignoreErrors` or in the route
      // boundary because it is the ONLY choke point both capture paths cross:
      // `RouteErrorBoundary` reports through `handleError`, and Sentry's own
      // global handlers report the raw `import()` rejection independently of any
      // of our code. A fix in either one alone leaves the other half of the
      // volume in place.
      if (isStaleChunkError(hint?.originalException ?? eventMessage(event))) {
        if (!hasStaleChunkRecoveryFailed()) return null
        event.tags = { ...event.tags, staleChunkRecovery: "failed" }
      }

      // Remove sensitive data from cookies
      if (event.request?.cookies) {
        const sensitiveCookies = ["__session", "clerk_session", "auth_token"]
        sensitiveCookies.forEach((cookie) => {
          if (event.request?.cookies?.[cookie]) {
            event.request.cookies[cookie] = "[Filtered]"
          }
        })
      }

      // Remove sensitive headers
      if (event.request?.headers) {
        const sensitiveHeaders = ["authorization", "cookie", "x-api-key", "api-key"]
        sensitiveHeaders.forEach((header) => {
          if (event.request?.headers?.[header]) {
            event.request.headers[header] = "[Filtered]"
          }
        })
      }

      // Remove sensitive form data
      if (event.request?.data) {
        const sensitiveFields = [
          "password",
          "secret",
          "token",
          "apiKey",
          "api_key",
          "accessToken",
          "refreshToken"
        ]
        if (typeof event.request.data === "object") {
          const data = event.request.data as Record<string, unknown>
          sensitiveFields.forEach((field) => {
            if (data[field]) {
              data[field] = "[Filtered]"
            }
          })
        }
      }

      // The embed iframe's URL carries the visitor's identity, `user_hash`
      // included, and the SDK copied it into `request.url` before this ran.
      return scrubEmbedIdentityFromEvent(event)
    },

    // `/Embed/abc` and `/embed/abc` are ONE route reaching Sentry under two
    // names, so they group as two issues and two percentile populations. This
    // rewrites the leading segment on the outbound transaction event and
    // nothing else — the widget id keeps its bytes, and no router, redirect or
    // guard reads this value. The capital spelling stays load-bearing in the
    // app; see `trace-sampling.ts`.
    //
    // Returning the event unconditionally is the point: `null` here DROPS the
    // transaction, so every path out of this function returns `event`.
    beforeSendTransaction(event) {
      if (typeof event.transaction === "string") {
        event.transaction = normalizeEmbedTransactionName(event.transaction)
      }
      return scrubEmbedIdentityFromEvent(event)
    },

    // A navigation or fetch breadcrumb names the embed URL as `from`/`to`/`url`.
    beforeBreadcrumb(breadcrumb) {
      return scrubEmbedIdentityFromBreadcrumb(breadcrumb)
    },

    ignoreErrors: IGNORED_ERRORS,

    // Set initial scope tags
    initialScope: {
      tags: {
        component: "frontend",
        runtime: "browser",
        framework: "react-spa"
      }
    }
  })

  console.log(`✅ Sentry client initialized for environment: ${SENTRY_ENVIRONMENT}`)
}
